import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
    /** Page title text */
    title: string;
    /** Optional description text below title */
    description?: React.ReactNode;
    /** Optional icon to display next to title */
    icon?: LucideIcon;
    /** Action buttons or controls (displayed on the right) */
    children?: React.ReactNode;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Reusable page header component with consistent styling
 *
 * @example
 * <PageHeader
 *   icon={Building2}
 *   title="Advertisers"
 *   description="Manage advertiser information"
 * >
 *   <Button variant="outline" onClick={handleRefresh}>
 *     <RefreshCw className="h-4 w-4 mr-2" />
 *     Refresh
 *   </Button>
 *   <Button onClick={handleCreate}>
 *     <Plus className="h-4 w-4 mr-2" />
 *     New Advertiser
 *   </Button>
 * </PageHeader>
 */
export function PageHeader({
    title,
    description,
    icon: Icon,
    children,
    className,
}: PageHeaderProps) {
    return (
        <div className={cn("flex flex-col gap-4 md:flex-row md:items-start md:gap-6 mb-6", className)}>
            <div className="shrink-0">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                    {Icon && <Icon className="h-8 w-8" />}
                    {title}
                </h1>
                {description && (
                    <p className="text-gray-500 mt-1">
                        {description}
                    </p>
                )}
            </div>
            {children && (
                <div className="flex items-end gap-3 flex-wrap">
                    {children}
                </div>
            )}
        </div>
    );
}
